import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './EmployeeDetailModal.css';
import CategoryRequirementsModal from '../category-requirements/CategoryRequirementsModal';
import ScrumNotesPanel from '../scrum-notes/ScrumNotesPanel';
import CareerPathPanel from '../career-path/CareerPathPanel';
import PerformancePanel from '../performance/PerformancePanel';
import { Employee, PointEntry, CategoryInfo, EmployeeDetailModalProps, CategorySummary } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { ADMIN_NAMES } from '../../config/teamConfig';

const API_URL = (process.env.REACT_APP_API_URL as string) || 'http://localhost:3001/api';

const CATEGORIES: CategoryInfo[] = [
  {
    name: 'Code Quality',
    description: 'Adheres to SOLID, DRY, and KISS principles while balancing simplicity and flexibility',
    criteria: [
      'Adheres to SOLID, DRY, and KISS principles while balancing simplicity and flexibility',
      'Implements proper error handling',
      'Documents code appropriately',
      'Code is production-ready',
      'Includes necessary unit and integration tests',
      'Handles edge cases and failure scenarios',
      'Code is scalable and performant',
      'Avoids over-engineering solutions',
      'Designs code with scalability and performance in mind',
      'Applies design patterns appropriately',
      'Incorporates proper input validation, sanitization, and security best practices'
    ]
  },
  {
    name: 'Code Standards',
    description: 'Adheres to company-wide code guidelines',
    criteria: [
      'Adheres to company-wide code guidelines',
      'Uses consistent naming conventions',
      'Follows best practices for code structure',
      'Code is compatibile with target environments',
      'Avoids deprecated methods or libraries',
      'Writes code that passes lint checks',
      'Maintains consistent formatting across files',
      'Uses version control best practices (commits, branching, etc.)',
      'Minimizes duplicate code through refactoring',
      'Manages accessibility and compliance where applicable',
      'Keeps code modular and reusable',
      'Follows secure coding standards'
    ]
  },
  {
    name: 'Deliverability',
    description: 'Staying on scope',
    criteria: [
      'Staying on scope',
      'Staying within task estimates',
      'Delivers requested features without scope creep',
      'Minimizes rework and defects',
      'Delivers features ready for deployment',
      'Balances speed with quality appropriately',
      'Completes end-to-end implementations',
      'Deliverables are properly tested',
      'Handles production deployments smoothly',
      'Resolves issues quickly post-deployment',
      'Delivers work that requires minimal fixes'
    ]
  },
  {
    name: 'Reliability',
    description: 'At desk on time each day',
    criteria: [
      'At desk on time each day',
      'Tracking time accurately in intranet',
      'Manages scope changes appropriately',
      'Meets deadlines consistently',
      'Provides early warning of delays',
      'Prioritizes work effectively',
      'Maintains consistent availability',
      'Communicates schedule changes in advance',
      'Responsive to communications',
      'Assigns time to correct projects / tasks ids',
      'Time logged accurately reflects actual hours',
      'Available during expected work hours',
      'Follows through on commitments',
      'Attends required meetings and on time for them'
    ]
  },
  {
    name: 'Problem Solving',
    description: 'Identifies problems proactively',
    criteria: [
      'Identifies problems proactively',
      'Solves problems independently',
      'Takes ownership of issues',
      'Suggests process improvements',
      'Breaks down complex tasks into smaller pieces',
      'Identifies when estimate will be exceeded',
      'Re-estimates appropriately when complexity changes',
      'Thinks critically about solutions',
      'Researches issues before escalating',
      'Learns from mistakes',
      'Debugs systematically'
    ]
  },
  {
    name: 'Initiative',
    description: 'Takes on tasks without being asked',
    criteria: [
      'Takes on tasks without being asked',
      'Breaking down task before exceeding estimate',
      'Conducts self-review before submitting code',
      'Identifies opportunities for improvement',
      'Proposes innovative solutions',
      'Goes beyond assigned responsibilities',
      'Anticipates future needs and problems',
      'Volunteers for challenging projects',
      'Drives initiatives to completion',
      'Self-directs work when priorities are clear',
      'Seeks out additional responsibilities',
      'Helps unblock others proactively'
    ]
  },
  {
    name: 'Collaboration',
    description: 'Working well with teams and / or clients',
    criteria: [
      'Working well with teams and / or clients',
      'Ability to take directive',
      'Contributes positively to team dynamics',
      'Participates actively in team meetings',
      'Maintains professional client relationships',
      'Accepts feedback constructively',
      'Implements requested changes',
      'Adapts to changing priorities',
      'Collaborates effectively in pair programming',
      'Provides constructive code reviews',
      'Open to different approaches'
    ]
  },
  {
    name: 'Communication',
    description: 'Letting someone know about issues when appropriate',
    criteria: [
      'Letting someone know about issues when appropriate',
      'Notifying about exceeding task estimates',
      'Clarifies requirements before starting task',
      'Provides accurate initial estimates',
      'Seeks help before significantly exceeding estimates',
      'Escalates issues appropriately',
      'Provides context when reporting issues',
      'Suggests potential solutions',
      'Explains reasons for estimate overruns',
      'Provides helpful status updates'
    ]
  },
  {
    name: 'Professional Development',
    description: 'Continuous learning and growth',
    criteria: [
      'Seeks out learning opportunities',
      'Stays current with industry trends',
      'Shares knowledge with team',
      'Mentors junior developers',
      'Attends training and conferences',
      'Contributes to team knowledge base'
    ]
  }
];

interface PointDateInfo {
  date: string;
  total: number;
}

interface GroupedPointResponse {
  date: string;
  category: string;
  total_points: number;
  count: number;
  notes: string | null;
}

const EmployeeDetailModal: React.FC<EmployeeDetailModalProps> = ({ employee, onClose, onUpdate }) => {
  const { user } = useAuth();
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(employee);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [pointsByDate, setPointsByDate] = useState<PointEntry[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [pointsToAdd, setPointsToAdd] = useState<number>(1);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [categorySummary, setCategorySummary] = useState<CategorySummary[]>([]);
  const [viewingCategory, setViewingCategory] = useState<CategoryInfo | null>(null);
  const [activeTab, setActiveTab] = useState<'points' | 'scrum' | 'career' | 'performance'>('points');
  const [datesWithPoints, setDatesWithPoints] = useState<PointDateInfo[]>([]);

  // Format date to YYYY-MM-DD string
  const formatDateString = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Check if current user is an admin
  const isAdmin = user?.username 
    ? ADMIN_NAMES.some(name => name.toLowerCase() === user.username.toLowerCase())
    : false;

  // Update local employee state when prop changes
  useEffect(() => {
    setCurrentEmployee(employee);
  }, [employee]);

  const fetchUpdatedEmployee = async (employeeId?: number) => {
    const id = employeeId || currentEmployee?.id;
    if (!id) return;
    try {
      const response = await axios.get<Employee>(`${API_URL}/employees/${id}`);
      setCurrentEmployee(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching updated employee:', err);
      return null;
    }
  };

  useEffect(() => {
    if (employee?.id) {
      fetchUpdatedEmployee(employee.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  useEffect(() => {
    if (currentEmployee) {
      fetchPointsForDate();
      fetchCategorySummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee?.id, selectedDate]);

  // Fetch all dates that have points for this employee
  useEffect(() => {
    if (currentEmployee) {
      fetchDatesWithPoints();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee?.id]);

  const fetchPointsForDate = async () => {
    if (!currentEmployee) return;
    const dateStr = formatDateString(selectedDate);
    try {
      const response = await axios.get<PointEntry[]>(
        `${API_URL}/points/employee/${currentEmployee.id}/date/${dateStr}`
      );
      setPointsByDate(response.data);
    } catch (err) {
      console.error('Error fetching points:', err);
    }
  };

  const fetchCategorySummary = async () => {
    if (!currentEmployee) return;
    try {
      const response = await axios.get(`${API_URL}/points/employee/${currentEmployee.id}/summary`);
      setCategorySummary(response.data);
    } catch (err) {
      console.error('Error fetching category summary:', err);
    }
  };

  const fetchDatesWithPoints = async () => {
    if (!currentEmployee) return;
    try {
      const response = await axios.get<GroupedPointResponse[]>(
        `${API_URL}/points/employee/${currentEmployee.id}/grouped`
      );
      
      // Aggregate points by date (the API returns by date+category)
      const dateMap = new Map<string, number>();
      response.data.forEach(item => {
        const current = dateMap.get(item.date) || 0;
        dateMap.set(item.date, current + item.total_points);
      });
      
      const aggregated: PointDateInfo[] = Array.from(dateMap.entries()).map(([date, total]) => ({
        date,
        total
      }));
      
      setDatesWithPoints(aggregated);
    } catch (err) {
      console.error('Error fetching dates with points:', err);
    }
  };

  // Check if a date has points
  const getPointsForDate = (date: Date): number => {
    const dateStr = formatDateString(date);
    // Compare just the date part (YYYY-MM-DD) in case DB returns datetime
    const found = datesWithPoints.find(d => {
      const dbDate = d.date.split('T')[0]; // Handle both "2025-01-29" and "2025-01-29T00:00:00"
      return dbDate === dateStr;
    });
    return found ? found.total : 0;
  };

  // Custom day content for the date picker
  const renderDayContents = (day: number, date: Date) => {
    const points = getPointsForDate(date);
    return (
      <div className="date-picker-day">
        <span>{day}</span>
        {points !== 0 && (
          <span className={`point-indicator ${points > 0 ? 'positive' : 'negative'}`}>
            •
          </span>
        )}
      </div>
    );
  };

  const handleAddPoints = async () => {
    if (!currentEmployee || !selectedCategory) {
      alert('Please select a category');
      return;
    }

    const employeeId = currentEmployee.id;
    const dateStr = formatDateString(selectedDate);
    setLoading(true);
    try {
      await axios.post(`${API_URL}/points/employee/${employeeId}`, {
        category: selectedCategory,
        points: pointsToAdd,
        date: dateStr,
        notes: notes || null
      });
      
      // Optimistically update the points in local state
      setCurrentEmployee(prev => prev ? {
        ...prev,
        points: prev.points + pointsToAdd
      } : null);
      
      setNotes('');
      setPointsToAdd(1);
      setSelectedCategory('');
      
      // Fetch updated data from server to ensure accuracy
      await Promise.all([
        fetchUpdatedEmployee(employeeId),
        fetchPointsForDate(),
        fetchCategorySummary(),
        fetchDatesWithPoints()
      ]);
      
      onUpdate(); // Refresh employee list in parent
    } catch (err) {
      console.error('Error adding points:', err);
      // Revert optimistic update on error
      await fetchUpdatedEmployee(employeeId);
      alert('Failed to add points');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePoint = async (pointId: number) => {
    if (!window.confirm('Are you sure you want to delete this point entry?')) {
      return;
    }

    if (!currentEmployee) return;
    const employeeId = currentEmployee.id;
    
    // Get the point value before deleting for optimistic update
    const pointToDelete = pointsByDate.find(p => p.id === pointId);
    const pointsToRestore = pointToDelete ? -pointToDelete.points : 0;

    try {
      await axios.delete(`${API_URL}/points/${pointId}`);
      
      // Optimistically update the points in local state
      if (pointToDelete) {
        setCurrentEmployee(prev => prev ? {
          ...prev,
          points: prev.points + pointsToRestore
        } : null);
      }
      
      // Fetch updated data from server
      await Promise.all([
        fetchUpdatedEmployee(employeeId),
        fetchPointsForDate(),
        fetchCategorySummary(),
        fetchDatesWithPoints()
      ]);
      
      onUpdate();
    } catch (err) {
      console.error('Error deleting point:', err);
      // Revert optimistic update on error
      await fetchUpdatedEmployee(employeeId);
      alert('Failed to delete point');
    }
  };

  if (!currentEmployee) return null;

  const getCategoryTotal = (categoryName: string) => {
    const summary = categorySummary.find(s => s.category === categoryName);
    return summary ? summary.total_points : 0;
  };

  const getPointsForDateAndCategory = (categoryName: string) => {
    return pointsByDate
      .filter(p => p.category === categoryName)
      .reduce((sum, p) => sum + p.points, 0);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{currentEmployee.name}</h2>
            <p>Performance & Scrum Tracker</p>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="employee-info">
            <div className="info-item">
              <span className="label">Department:</span>
              <span>{currentEmployee.department}</span>
            </div>
            <div className="info-item">
              <span className="label">Position:</span>
              <span>{currentEmployee.position}</span>
            </div>
            <div className="info-item">
              <span className="label">Total Points:</span>
              <span className="total-points">{currentEmployee.points}</span>
            </div>
          </div>

          <div className="modal-tabs">
            <button
              className={`modal-tab ${activeTab === 'points' ? 'active' : ''}`}
              onClick={() => setActiveTab('points')}
            >
              Point Tracking
            </button>
            {isAdmin && (
              <button
                className={`modal-tab ${activeTab === 'scrum' ? 'active' : ''}`}
                onClick={() => setActiveTab('scrum')}
              >
                Scrum Log
              </button>
            )}
            <button
              className={`modal-tab ${activeTab === 'career' ? 'active' : ''}`}
              onClick={() => setActiveTab('career')}
            >
              Career Path
            </button>
            <button
              className={`modal-tab ${activeTab === 'performance' ? 'active' : ''}`}
              onClick={() => setActiveTab('performance')}
            >
              Performance
            </button>
          </div>

          <div className={`tab-panel ${activeTab === 'points' ? 'active' : ''}`}>
            <div className="date-selector">
              <label>Date:</label>
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => date && setSelectedDate(date)}
                dateFormat="MM/dd/yyyy"
                renderDayContents={renderDayContents}
                className="date-picker-input"
                calendarClassName="points-calendar"
              />
              <div className="date-legend">
                <span className="legend-item"><span className="point-indicator positive">•</span> Points awarded</span>
                <span className="legend-item"><span className="point-indicator negative">•</span> Points deducted</span>
              </div>
            </div>

            {isAdmin && (
              <div className="add-points-section">
                <h3>Award / Deduct Points</h3>
                <div className="points-help-text">
                  Enter a positive number to award points, or a negative number to deduct points
                </div>
                <div className="add-points-form">
                  <div className="form-group">
                    <label>Category:</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="">Select a category</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Points:</label>
                    <input
                      type="number"
                      value={pointsToAdd}
                      onChange={(e) => setPointsToAdd(parseInt(e.target.value) || 0)}
                      min="-100"
                      max="100"
                      placeholder="+1 or -1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes:</label>
                    <input
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional notes..."
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleAddPoints}
                    disabled={loading || !selectedCategory || pointsToAdd === 0}
                  >
                    {loading ? 'Processing...' : pointsToAdd >= 0 ? 'Award Points' : 'Deduct Points'}
                  </button>
                </div>
              </div>
            )}

            <div className="categories-section">
              <h3>Categories & Points</h3>
              <div className="categories-grid">
                {CATEGORIES.map(category => {
                  const categoryTotal = getCategoryTotal(category.name);
                  const dateTotal = getPointsForDateAndCategory(category.name);
                  const categoryPoints = pointsByDate.filter(p => p.category === category.name);

                  return (
                    <div 
                      key={category.name} 
                      className="category-card"
                      onClick={() => setViewingCategory(category)}
                    >
                      <div className="category-header">
                        <h4>{category.name}</h4>
                        <div className="category-totals">
                          <span className="total">Total: {categoryTotal}</span>
                          <span className="date-total">Today: {dateTotal}</span>
                        </div>
                      </div>
                      <div className="category-description">
                        {category.description}
                      </div>
                      <div className="category-click-hint">
                        Click to view all requirements →
                      </div>
                      {categoryPoints.length > 0 && (
                        <div className="category-points-list" onClick={(e) => e.stopPropagation()}>
                          {categoryPoints.map(point => (
                            <div key={point.id} className="point-entry">
                              <span className="point-value">{point.points > 0 ? '+' : ''}{point.points}</span>
                              {point.notes && <span className="point-notes">{point.notes}</span>}
                              {isAdmin && (
                                <button
                                  className="delete-point-btn"
                                  onClick={() => handleDeletePoint(point.id)}
                                  title="Delete point"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {isAdmin && (
            <div className={`tab-panel ${activeTab === 'scrum' ? 'active' : ''}`}>
              <ScrumNotesPanel
                employeeId={currentEmployee.id}
                employeeName={currentEmployee.name}
                isAdmin={isAdmin}
                intranetEmployeeId={currentEmployee.intranet_id || undefined}
              />
            </div>
          )}

          <div className={`tab-panel ${activeTab === 'career' ? 'active' : ''}`}>
            <CareerPathPanel
              employeeId={currentEmployee.id}
              employeeName={currentEmployee.name}
              isAdmin={isAdmin}
            />
          </div>

          <div className={`tab-panel ${activeTab === 'performance' ? 'active' : ''}`}>
            <PerformancePanel
              employeeId={currentEmployee.id}
              employeeName={currentEmployee.name}
              intranetEmployeeId={currentEmployee.intranet_id || undefined}
            />
          </div>
        </div>
      </div>

      {viewingCategory && (
        <CategoryRequirementsModal
          category={viewingCategory}
          onClose={() => setViewingCategory(null)}
        />
      )}
    </div>
  );
};

export default EmployeeDetailModal;

